import jsPDF from "jspdf";
import { euro, germanDate } from "@/lib/format";

const line = (doc, y) => {
    doc.setDrawColor(220);
    doc.line(16, y, 194, y);
};

const header = (doc, title, subtitle = "", entity = null) => {
    // Briefkopf — Entität als Absender
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(17, 24, 39);
    doc.text(entity?.name || "Tplex", 16, 18);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(107, 114, 128);
    const meta = [];
    if (entity?.rechtsform) meta.push(entity.rechtsform);
    if (entity?.steuernummer) meta.push(`Steuer-Nr. ${entity.steuernummer}`);
    if (meta.length) doc.text(meta.join(" · "), 16, 23.5);
    else doc.text("Verwaltet via Tplex", 16, 23.5);
    if (entity?.iban) {
        doc.setFontSize(7.5);
        doc.text(`IBAN: ${entity.iban}`, 16, 27.5);
    }

    // Title right
    doc.setFontSize(11);
    doc.setTextColor(17, 24, 39);
    doc.setFont("helvetica", "bold");
    doc.text(title.toUpperCase(), 194, 18, { align: "right" });
    if (subtitle) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(107, 114, 128);
        doc.text(subtitle, 194, 24, { align: "right" });
    }
    line(doc, 32);
};

const kv = (doc, y, k, v) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(107, 114, 128);
    doc.text(k, 16, y);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(17, 24, 39);
    doc.text(String(v ?? "—"), 80, y);
};

export const exportKuendigungsbestaetigung = ({ turnover, unit, property, entity, oldTenant }) => {
    const doc = new jsPDF();
    header(doc, "Kündigungsbestätigung", `Dokument vom ${germanDate(new Date().toISOString())}`, entity);
    const t = turnover.termination || {};
    let y = 44;
    doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(17, 24, 39);
    doc.text("Sehr geehrte/r " + (oldTenant?.full_name || "Mieter/in") + ",", 16, y); y += 10;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(55, 65, 81);
    const text = doc.splitTextToSize(
        `hiermit bestätigen wir den Eingang Ihrer Kündigung zum Mietverhältnis über die Mieteinheit ${unit?.unit_label || ""}, ${property?.address || unit?.address || ""}.`,
        178,
    );
    doc.text(text, 16, y); y += text.length * 5 + 4;

    kv(doc, y, "Eingang der Kündigung", germanDate(t.kuendigung_eingang)); y += 7;
    kv(doc, y, "Kündigungsdatum Mieter", germanDate(t.kuendigung_datum_mieter)); y += 7;
    kv(doc, y, "Kündigungsfrist", `${t.kuendigungsfrist_monate || 3} Monate`); y += 7;
    kv(doc, y, "Auszugsdatum (berechnet)", germanDate(t.auszugsdatum_berechnet)); y += 7;
    kv(doc, y, "Strategie", t.strategie || "—"); y += 10;

    doc.setFontSize(10);
    const paragraph = doc.splitTextToSize(
        "Das Mietverhältnis endet unter Berücksichtigung der vertraglichen Kündigungsfrist zum oben genannten Auszugsdatum. Wir werden uns rechtzeitig mit Ihnen zur Übergabe der Wohnung in Verbindung setzen. Bitte teilen Sie uns Ihre neue Anschrift für die Kautionsabrechnung mit.",
        178,
    );
    doc.text(paragraph, 16, y); y += paragraph.length * 5 + 8;

    doc.text("Mit freundlichen Grüßen", 16, y); y += 14;
    doc.setFont("helvetica", "bold"); doc.text(entity?.kontakt_name || "—", 16, y);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(107, 114, 128);
    doc.text(entity?.name || "Tplex", 16, y + 5);

    doc.save(`Kuendigungsbestaetigung_${oldTenant?.full_name?.replace(/\s+/g, "_") || "Mieter"}.pdf`);
};

export const exportUebergabeprotokoll = ({ turnover, handover, variante = "Übergabe", unit, property, entity, oldTenant, newTenant, accessories = [] }) => {
    const doc = new jsPDF();
    const h = handover || turnover.handover_auszug || turnover.handover || {};
    header(doc, `Übergabeprotokoll · ${variante}`, germanDate(h.datum) || "", entity);

    let y = 42;
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(17, 24, 39);
    doc.text("Objekt & Parteien", 16, y); y += 6;
    kv(doc, y, "Immobilie", `${property?.label || ""} — ${property?.address || ""}`); y += 6;
    kv(doc, y, "Mieteinheit", `${unit?.unit_label || ""} (${unit?.typ || "wohnung"})`); y += 6;
    if (accessories && accessories.length) {
        kv(doc, y, "Mit Zubehör", accessories.map((a) => a.unit_label || a.typ).join(", "));
        y += 6;
    }
    kv(doc, y, "Alter Mieter", oldTenant?.full_name || "—"); y += 6;
    kv(doc, y, "Neuer Mieter", h.neuer_mieter_name || newTenant?.full_name || "—"); y += 6;
    kv(doc, y, "Datum / Uhrzeit", `${germanDate(h.datum)} ${h.uhrzeit || ""}`); y += 8;

    line(doc, y); y += 6;
    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text("Zählerstände", 16, y); y += 6;
    (h.zählerstände || []).forEach((m) => {
        kv(doc, y, `${m.art || ""} (Nr. ${m.zaehler_nr || "—"})`, `${m.stand || "—"}  ·  ${germanDate(m.datum)}`);
        y += 6;
    });
    y += 4;
    line(doc, y); y += 6;

    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text("Raumzustand", 16, y); y += 6;
    (h.raeume || []).forEach((r) => {
        kv(doc, y, r.raum, `${r.zustand}${r.bemerkung ? " — " + r.bemerkung : ""}`);
        y += 6;
    });
    y += 4;
    line(doc, y); y += 6;

    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text("Schlüssel & Handsender", 16, y); y += 6;
    (h.schluessel || []).forEach((k) => {
        kv(doc, y, k.art, `${k.anzahl} Stück${k.bemerkung ? " — " + k.bemerkung : ""}`);
        y += 6;
    });
    // Handsender für Garagen-Zubehör
    const garageAccessories = (accessories || []).filter((a) => a.typ === "garage" || a.typ === "stellplatz");
    if (garageAccessories.length > 0) {
        kv(doc, y, "Handsender (Zubehör)", `${h.handsender ?? 0} Stück — für ${garageAccessories.length} Zubehöreinheit(en)`);
        y += 6;
    }
    y += 6;

    // Signatures
    doc.setFont("helvetica", "bold"); doc.setFontSize(10);
    doc.text("Unterschrift Mieter", 16, y);
    doc.text("Unterschrift Vermieter", 110, y);
    y += 4;
    if (h.unterschrift_mieter) { try { doc.addImage(h.unterschrift_mieter, "PNG", 16, y, 80, 24); } catch (_) {} }
    if (h.unterschrift_vermieter) { try { doc.addImage(h.unterschrift_vermieter, "PNG", 110, y, 80, 24); } catch (_) {} }
    y += 28;
    doc.setDrawColor(180); doc.line(16, y, 94, y); doc.line(110, y, 190, y);

    doc.save(`Uebergabeprotokoll_${unit?.unit_label || property?.address || "Einheit"}.pdf`);
};

export const exportWohnungsgeberbestaetigung = ({ turnover, unit, property, entity, newTenant }) => {
    const doc = new jsPDF();
    header(doc, "Wohnungsgeberbestätigung", "§ 19 BMG", entity);
    const h = turnover.handover || {};
    let y = 44;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(55, 65, 81);
    doc.text("Die/der unterzeichnete Wohnungsgeber bestätigt:", 16, y); y += 10;

    kv(doc, y, "Wohnungsgeber", entity?.name || "Tplex"); y += 7;
    kv(doc, y, "Rechtsform", entity?.rechtsform || "—"); y += 7;
    kv(doc, y, "Anschrift Wohnung", `${unit?.unit_label || ""} — ${property?.address || ""}`); y += 7;
    kv(doc, y, "Einzugsdatum", germanDate(h.datum)); y += 7;
    kv(doc, y, "Name Mieter", h.neuer_mieter_name || newTenant?.full_name || "—"); y += 12;

    const p = doc.splitTextToSize(
        "Diese Bestätigung dient der Vorlage bei der Meldebehörde. Die Wohnung wird bezogen von der oben genannten Person. Diese Bestätigung ersetzt nicht den Mietvertrag.",
        178,
    );
    doc.text(p, 16, y); y += p.length * 5 + 18;
    doc.setFont("helvetica", "bold"); doc.text(entity?.kontakt_name || "—", 16, y);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(107, 114, 128);
    doc.text(entity?.name || "Tplex", 16, y + 5);

    doc.save(`Wohnungsgeberbestaetigung_${(h.neuer_mieter_name || newTenant?.full_name || "Mieter").replace(/\s+/g, "_")}.pdf`);
};

export const exportKautionsabrechnung = ({ turnover, unit, property, entity, oldTenant }) => {
    const doc = new jsPDF();
    header(doc, "Kautionsabrechnung", germanDate(new Date().toISOString()), entity);
    const d = turnover.deposit || {};
    let y = 44;
    kv(doc, y, "Mieter", oldTenant?.full_name || "—"); y += 7;
    kv(doc, y, "Einheit", `${unit?.unit_label || ""} — ${property?.address || ""}`); y += 7;
    if (unit?.typ === "gewerbe" && unit?.mwst_pflicht) {
        kv(doc, y, "Hinweis", "Gewerbe · Option zur USt-Pflicht (§9 UStG)"); y += 7;
    }
    kv(doc, y, "Basisbetrag (Kaution)", euro(d.basisbetrag)); y += 10;

    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(17, 24, 39);
    doc.text("Abzüge", 16, y); y += 6;
    (d.abzüge || []).forEach((a) => {
        doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(55, 65, 81);
        doc.text(a.position, 16, y);
        doc.text(euro(a.betrag), 194, y, { align: "right" });
        y += 6;
    });
    if (d.nachzahlung_nk) {
        doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(55, 65, 81);
        doc.text("Nachzahlung Nebenkosten", 16, y);
        doc.text(euro(d.nachzahlung_nk), 194, y, { align: "right" });
        y += 6;
    }
    y += 2; line(doc, y); y += 8;

    doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(17, 24, 39);
    doc.text("Auszahlungsbetrag", 16, y);
    doc.text(euro(d.restbetrag), 194, y, { align: "right" });
    y += 10;

    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(107, 114, 128);
    kv(doc, y, "IBAN", d.iban || "—"); y += 6;
    kv(doc, y, "Auszahlungsdatum", germanDate(d.auszahlungsdatum)); y += 6;
    kv(doc, y, "Status", d.status || "offen"); y += 10;
    if (entity?.iban) {
        doc.setFontSize(8); doc.text(`Auszahlung von Konto: ${entity.iban} (${entity.name})`, 16, y);
    }

    doc.save(`Kautionsabrechnung_${oldTenant?.full_name?.replace(/\s+/g, "_") || "Mieter"}.pdf`);
};
